import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { Card, Form, Button, Row, Col, Spinner, Alert, Table, Modal } from "react-bootstrap";
import * as signalR from "@microsoft/signalr";
import PageHeader from "../shared/PageHeader";
import ImagePreviewModal from "../shared/ImagePreviewModal";
import PageContainer from "../shared/PageContainer";
import FieldRenderer, { FieldSchema } from "./FieldRenderer";
import ProductModal from "./ProductModal";
import { AvailableProduct, OrderEditHistory, Order } from "../../types";
import { buildAppPath } from "../../config/appBasePath";
import { ordersApi, productsApi, SUPPLIER_API_URL } from "../../services/services";
import { useRequiredAuth } from "../shared/AuthContext";

// Maps display names (as stored in older DB records) to API slugs
const TYPE_TO_SLUG: Record<string, string> = {
  "Celebration Cake": "celebration-cake",
  "Wedding Cake": "wedding-cake",
  "Daily Cake Order": "daily-order-cakes",
  "Standard": "standard-order",
};

const toSlug = (type: string): string => TYPE_TO_SLUG[type] || type;

interface SectionSchema {
  title: string;
  fields: FieldSchema[];
}

interface FormSchema {
  type: string;
  title: string;
  sections: SectionSchema[];
  availableProducts?: AvailableProduct[];
}

const computeField = (
  expression: string,
  data: Record<string, any>
): number => {
  try {
    const tokens = expression.split(/\s*([+-])\s*/);
    let result = parseFloat(data[tokens[0]] || "0") || 0;
    for (let i = 1; i < tokens.length; i += 2) {
      const operator = tokens[i];
      const fieldName = tokens[i + 1];
      const value = parseFloat(data[fieldName] || "0") || 0;
      if (operator === "+") result += value;
      else if (operator === "-") result -= value;
    }
    return Math.round(result * 100) / 100;
  } catch {
    return 0;
  }
};

const DynamicOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { type, id } = useParams<{ type?: string; id?: string }>();
  const { user } = useRequiredAuth();

  // Determine modes
  const isPrintMode = location.pathname.includes("/orders/print/");
  const isCreateMode = location.pathname.includes("/orders/create/");
  const isViewMode = (!!id && !isCreateMode) || isPrintMode;
  const [isEditMode, setIsEditMode] = useState(searchParams.get("edit") === "true");
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  // Product modal state
  const [showProductModal, setShowProductModal] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [productQuantities, setProductQuantities] = useState<
    Record<number, number>
  >({});
  const [productSearch, setProductSearch] = useState("");

  // Status History Logs
  const [logs, setLogs] = useState<any[]>([]);

  // Edit history
  const [editHistory, setEditHistory] = useState<OrderEditHistory[]>([]);
  const [showPreviousVersionModal, setShowPreviousVersionModal] = useState(false);
  const [previousVersionData, setPreviousVersionData] = useState<Record<string, any> | null>(null);
  const [previousVersionLoading, setPreviousVersionLoading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load schema from API and initialize form (or load existing order in view mode)
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        // View mode: load existing order and its schema
        if (isViewMode && id) {
          const order = await ordersApi.getOrderById(parseInt(id, 10));
          if (!order) {
            setSchemaError(`Order not found: ${id}`);
            setLoading(false);
            return;
          }

          let loadedSchema: FormSchema | null = null;

          const orderTypeSlug = toSlug(order.type);

          // Standard orders use a hardcoded schema (same as client)
          if (orderTypeSlug === "standard-order") {
            loadedSchema = {
              type: "standard-order",
              title: "Standard Order",
              sections: [
                {
                  title: "Order Details",
                  fields: [
                    { name: "dateRequired", label: "Date Required", type: "date" },
                    { name: "notes", label: "Additional Notes", type: "textarea" },
                  ]
                },
                {
                  title: "Products",
                  fields: [
                    { name: "products", label: "Products", type: "productList" }
                  ]
                }
              ]
            };
          } else {
            // Dynamic orders - fetch schema from API
            try {
              loadedSchema = await ordersApi.getFormSchema(orderTypeSlug);
            } catch {
              // ignore
            }
          }

          setCurrentOrder(order);

          // Fetch logs and edit history
          try {
            const statusLogs: any = await ordersApi.getClientOrderLogs(parseInt(id, 10));
            if (statusLogs) setLogs(statusLogs);
          } catch {
            // ignore
          }

          try {
            const history = await ordersApi.getEditHistory(parseInt(id, 10));
            if (history) setEditHistory(history);
          } catch {
            // ignore
          }

          if (!loadedSchema) {
            setSchemaError(`Form schema not found for type: ${order.type}`);
            setLoading(false);
            return;
          }

          setSchema(loadedSchema);
          setSchemaError(null);

          // Parse stored form data JSON
          if (order.formData) {
            try {
              const parsedFormData = JSON.parse(order.formData);
              setFormData(parsedFormData);
            } catch {
              // Fallback: initialize with basic order data
              setFormData({
                clientId: order.clientId,
                type: order.type,
                dateOrdered: order.dateOrdered,
                dateRequired: order.dateRequired,
                total: order.total,
                status: order.status,
              });
            }
          }
        } else {
          // Create mode: load schema and initialize empty form
          if (!type) {
            setSchemaError("Order type not specified");
            setLoading(false);
            return;
          }

          let loadedSchema: FormSchema;

          // Standard order uses a hardcoded schema with product list
          if (type === "standard-order") {
            loadedSchema = {
              type: "standard-order",
              title: "Standard Order",
              sections: [
                {
                  title: "Order Details",
                  fields: [
                    { name: "dateRequired", label: "Date Required", type: "date" },
                    { name: "notes", label: "Additional Notes", type: "textarea" },
                  ]
                },
                {
                  title: "Products",
                  fields: [
                    { name: "products", label: "Products", type: "productList" }
                  ]
                }
              ]
            };
          } else {
            loadedSchema = await ordersApi.getFormSchema(type);
          }

          if (!loadedSchema) {
            setSchemaError(`Form schema not found for type: ${type}`);
            setLoading(false);
            return;
          }

          // Load supplier's own products for product list fields
          const hasProductList = loadedSchema.sections.some((section: SectionSchema) =>
            section.fields.some(
              (field: FieldSchema) =>
                field.type === "productList" || field.type === "inlineProductList"
            )
          );

          if (hasProductList) {
            try {
              const apiProducts = await productsApi.getProducts();
              loadedSchema.availableProducts = apiProducts
                .filter((p) => p.availability !== false)
                .map((p) => ({
                  code: p.productCode || "",
                  name: p.name || "",
                  size: `${p.weight ?? 0} kg`,
                  supplierId: p.supplierId,
                }));
            } catch {
              loadedSchema.availableProducts = [];
            }
          }

          setSchema(loadedSchema);
          setSchemaError(null);

          // Initialize form data with default values
          const initialData: Record<string, any> = {};
          loadedSchema.sections.forEach((section: SectionSchema) => {
            section.fields.forEach((field: FieldSchema) => {
              if (field.defaultValue !== undefined) {
                initialData[field.name] = field.defaultValue;
              } else if (field.type === "quantity") {
                initialData[field.name] = 1;
              } else {
                initialData[field.name] = "";
              }
            });
          });
          setFormData(initialData);
        }
      } catch {
        setSchemaError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [type, id, isViewMode, isCreateMode]);

  // SignalR Connection for Real-time Updates (Moved to top-level)
  useEffect(() => {
    if (!isViewMode || !id) return;

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${SUPPLIER_API_URL.replace("/api", "")}/hub/notifications`)
      .withAutomaticReconnect()
      .build();

    newConnection.start()
      .then(() => {
        // Join the specific order group
        newConnection.invoke("JoinOrderGroup", id.toString())
          .catch(() => {});

        // Listen for status changes
        newConnection.on("OrderStatusChanged", (newLog: any) => {
          setLogs((prevLogs: any) => {
            // Prevent duplicates if necessary, or just prepend
            if (prevLogs.some((l: any) => l.id === newLog.id)) return prevLogs;
            return [newLog, ...prevLogs];
          });
        });
      })
      .catch(() => {});

    return () => {
      if (newConnection) {
        newConnection.stop();
      }
    };
  }, [id, isViewMode]);

  // Computed fields
  useEffect(() => {
    if (!schema) return;

    const newFormData = { ...formData };
    let changed = false;

    schema.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.computed) {
          const computedValue = computeField(field.computed, formData);
          if (newFormData[field.name] !== computedValue) {
            newFormData[field.name] = computedValue;
            changed = true;
          }
        }
      });
    });

    if (changed) setFormData(newFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schema,
    formData.price,
    formData.extra,
    formData.delivery,
    formData.deposit,
    formData.total,
    computeField,
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent, saveAsDraft = false) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const requiredDateValue = formData.dateRequired || formData.requiredDate;

      const orderPayload = {
        type: schema?.type || type || "standard-order",
        dateRequired: requiredDateValue || new Date().toISOString(),
        total: parseFloat(formData.total || formData.balance || "0") || 0,
        status: saveAsDraft ? "Draft" : "Pending",
        formData: JSON.stringify(formData),
        supplierId: user.supplierId ? parseInt(String(user.supplierId), 10) : 0,
        clientId: 0, // Supplier creating order to itself
      };

      const result = await ordersApi.createOrder(orderPayload);
      if (result) {
        navigate("/orders");
      } else {
        setSubmitError("Failed to create order. Please try again.");
      }
    } catch {
      setSubmitError("An error occurred while creating the order.");
    } finally {
      setSubmitting(false);
    }
  };

  const addProductsFromModal = () => {
    if (!schema?.availableProducts) return;

    const newProducts = [...(formData.products || [])];
    schema.availableProducts.forEach((product, index) => {
      const qty = productQuantities[index];
      if (qty && qty > 0) {
        newProducts.push({ ...product, quantity: qty });
      }
    });

    setFormData((prev) => ({ ...prev, products: newProducts }));
    setProductQuantities({});
    setProductSearch("");
    setShowProductModal(false);
  };

  // Save edited order
  const handleSaveEdit = async () => {
    if (!currentOrder || !id) return;
    setSavingEdit(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Build the formData JSON string, ensuring all fields are included
      const formDataToSave = { ...formData };
      const formDataJson = JSON.stringify(formDataToSave);

      // Resolve dateRequired: prefer form field, fall back to original order
      let dateRequired = formData.dateRequired || formData.requiredDate || currentOrder.dateRequired;
      // Ensure it's a valid ISO string for the backend DateTime parser
      if (dateRequired) {
        const parsed = new Date(dateRequired);
        if (!isNaN(parsed.getTime())) {
          dateRequired = parsed.toISOString();
        }
      }

      await ordersApi.updateOrder({
        Id: parseInt(id, 10),
        dateRequired,
        total: parseFloat(formData.total || formData.balance || currentOrder.total || "0") || 0,
        formData: formDataJson,
      });

      setSaveSuccess(true);
      setIsEditMode(false);

      // Reload order from server to get the saved state
      const updatedOrder = await ordersApi.getOrderById(parseInt(id, 10));
      setCurrentOrder(updatedOrder);

      // Re-parse formData from the freshly saved order so the view reflects DB state
      if (updatedOrder.formData) {
        try {
          setFormData(JSON.parse(updatedOrder.formData));
        } catch {
          // keep current formData if parsing fails
        }
      }

      const updatedLogs = await ordersApi.getClientOrderLogs(parseInt(id, 10));
      setLogs(updatedLogs);

      const updatedHistory = await ordersApi.getEditHistory(parseInt(id, 10));
      setEditHistory(updatedHistory);

      // Remove ?edit=true from URL
      navigate(`/orders/view/${id}`, { replace: true });
    } catch (error: any) {
      setSaveError(error.message || "Failed to save changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setSaveError(null);
    setSaveSuccess(false);

    // Restore original form data
    if (currentOrder?.formData) {
      try {
        setFormData(JSON.parse(currentOrder.formData));
      } catch {}
    }

    navigate(`/orders/view/${id}`, { replace: true });
  };

  // View previous version
  const handleViewPreviousVersion = async (historyRecord: OrderEditHistory) => {
    setPreviousVersionLoading(true);
    setShowPreviousVersionModal(true);
    try {
      if (historyRecord.previousFormData) {
        const parsed = JSON.parse(historyRecord.previousFormData);
        setPreviousVersionData(parsed);
      } else {
        setPreviousVersionData({});
      }
    } catch {
      setPreviousVersionData({});
    } finally {
      setPreviousVersionLoading(false);
    }
  };

  const canEdit = isViewMode && !isPrintMode && currentOrder?.status === "Pending";

  // Loading state
  if (loading) {
    return (
      <div className="page-wrapper-center">
        <Spinner animation="border" />
      </div>
    );
  }

  // Schema not found or error
  if (!schema || schemaError) {
    return (
      <div className="page-wrapper">
        <PageHeader title="Manage" subTitle="Orders" />
        <Card>
          <Card.Body>
            <Alert variant="warning">
              {schemaError || `Form not found for order type: ${type}`}
            </Alert>
            <Button variant="secondary" onClick={() => navigate("/orders")}>
              Back to Orders
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Handle print preview
  const handlePrintPreview = () => {
    // Open specialized print route in new tab
    window.open(buildAppPath(`/orders/print/${id}`), "_blank");
  };

  // Immediate print for print mode page
  const handlePrint = () => {
    window.print();
  };

  return (
    <React.Fragment>
      {!isPrintMode ? (
        <PageContainer>
          <div className="page-wrapper">
            <div className="sub-header-title d-flex justify-content-between align-items-center">
              <span className="h3 d-block mb-0">
                {isCreateMode ? `New: ${schema.title}` : schema.title}
              </span>
              <div className="d-flex gap-2">
                {canEdit && !isEditMode && (
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => {
                      setIsEditMode(true);
                      navigate(`/orders/view/${id}?edit=true`, { replace: true });
                    }}
                    className="small px-3"
                  >
                    <i className="bi bi-pencil-square me-1"></i> Edit Order
                  </Button>
                )}
                {isViewMode && (
                  <>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => window.open(buildAppPath(`/orders/delivery-note/${id}`), "_blank")}
                      className="small px-3"
                    >
                      <i className="bi bi-printer me-1"></i> Print Preview
                    </Button>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={() => window.open(buildAppPath(`/orders/customer-print/${id}`), "_blank")}
                      className="small px-3 text-white"
                    >
                      <i className="bi bi-receipt me-1"></i> Customer Print
                    </Button>
                  </>
                )}
              </div>
            </div>

            {saveSuccess && (
              <Alert variant="success" dismissible onClose={() => setSaveSuccess(false)} className="mt-2">
                <i className="bi bi-check-circle me-1"></i> Order updated successfully.
              </Alert>
            )}
            {saveError && (
              <Alert variant="danger" dismissible onClose={() => setSaveError(null)} className="mt-2">
                <i className="bi bi-exclamation-triangle me-1"></i> {saveError}
              </Alert>
            )}

            {/* Form content */}
            <Form onSubmit={handleSubmit}>
              {schema.sections.map((section, sectionIdx) => (
                <div key={sectionIdx}>
                  <h5 className="section-title">{section.title}</h5>
                  {section.fields.map((field, fieldIdx) => (
                    <Row
                      key={fieldIdx}
                      className={`mb-2 ${field.type === "textarea" ? "align-items-start" : "align-items-center"}`}
                    >
                      <Col md={2}>
                        <Form.Label className="form-label-custom">{field.label}</Form.Label>
                      </Col>
                      <Col md={10}>
                        <FieldRenderer
                          field={field}
                          value={formData[field.name] ?? ""}
                          formData={formData}
                          setFormData={setFormData}
                          onShowProductModal={() => setShowProductModal(true)}
                          availableProducts={schema.availableProducts}
                          readOnly={isEditMode ? false : isViewMode}
                          onShowImagePreview={(src) => setPreviewImage(src)}
                          supplierEditMode={isEditMode}
                        />
                      </Col>
                    </Row>
                  ))}
                </div>
              ))}

              {submitError && !isViewMode && (
                <Alert variant="danger" className="mb-3 small">{submitError}</Alert>
              )}

              <div className="mt-4">
                {isEditMode && (
                  <>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      className="me-2 small px-3"
                      disabled={savingEdit}
                      onClick={handleSaveEdit}
                    >
                      {savingEdit ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : <><i className="bi bi-check-lg me-1"></i>Save Changes</>}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      className="me-2 small px-3"
                      disabled={savingEdit}
                      onClick={handleCancelEdit}
                    >
                      <i className="bi bi-x-lg me-1"></i>Cancel
                    </Button>
                  </>
                )}
                {isCreateMode && (
                  <>
                    <Button type="submit" variant="primary" size="sm" className="me-2 small px-3" disabled={submitting}>
                      {submitting ? <><Spinner animation="border" size="sm" className="me-1" />Creating...</> : <><i className="bi bi-plus-lg me-1"></i>Create Order</>}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      className="me-2 small px-3"
                      disabled={submitting}
                      onClick={(e) => handleSubmit(e as any, true)}
                    >
                      {submitting ? "Saving..." : "Save as Draft"}
                    </Button>
                  </>
                )}
                {!isViewMode && !isEditMode && !isCreateMode && (
                  <Button type="submit" variant="primary" size="sm" className="me-1 small px-3" disabled={submitting}>
                    {submitting ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : "Save"}
                  </Button>
                )}
                {!isEditMode && (
                  <Button
                    type="button" variant={isViewMode ? "secondary" : "danger"} size="sm"
                    onClick={() => navigate("/orders")} className="d-inline-block small px-4" disabled={submitting}
                  >
                    Back
                  </Button>
                )}
              </div>
            </Form>

            <ProductModal
              show={showProductModal} onClose={() => setShowProductModal(false)}
              availableProducts={schema.availableProducts || []} productQuantities={productQuantities}
              setProductQuantities={setProductQuantities} productSearch={productSearch}
              setProductSearch={setProductSearch} onAddProducts={addProductsFromModal}
            />

            {/* Status History Section (View Mode Only) */}
            {isViewMode && (
              <Card className="mt-4 shadow-sm border-0 mb-5">
                <Card.Header className="bg-white py-3 border-bottom-0">
                  <h5 className="mb-0 text-dark">Order History</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  {(logs && logs.length > 0) || editHistory.length > 0 ? (
                    <div className="table-responsive">
                      <Table className="mb-0 align-middle">
                        <thead className="bg-light">
                          <tr>
                            <th className="border-0 ps-4">Activity</th>
                            <th className="border-0">Done by</th>
                            <th className="border-0">Date Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map((log: any) => {
                            let badgeClass = "secondary";
                            const status = log.newStatus || "";
                            const notes = log.notes || "";
                            const isEditLog = notes.includes("edited by");
                            if (status.includes("Confirmed")) badgeClass = "success";
                            else if (status.includes("Production")) badgeClass = "warning text-dark";
                            else if (status.includes("Completed")) badgeClass = "info text-white";
                            else if (status.includes("Dispatched")) badgeClass = "primary";
                            else if (status.includes("Cancelled")) badgeClass = "danger";
                            else if (isEditLog) badgeClass = "info";
                            else if (status.includes("Updated")) badgeClass = "secondary";

                            // Find matching edit history record for this log
                            const matchingEdit = isEditLog ? editHistory.find((eh) => {
                              const editDate = new Date(eh.editedAt).getTime();
                              const logDate = new Date(log.changeDate).getTime();
                              return Math.abs(editDate - logDate) < 5000; // within 5 seconds
                            }) : null;

                            return (
                              <tr key={log.id}>
                                <td className="ps-4">
                                  <span className={`badge bg-${badgeClass} px-3 py-2 rounded-1`}>
                                    {isEditLog ? "Edited" : log.newStatus}
                                  </span>
                                </td>
                                <td>
                                  <div>
                                    <div className="d-flex align-items-center">
                                      <i className="bi bi-person-fill me-2 text-dark"></i>
                                      <span>{log.changedBy}</span>
                                    </div>
                                    {isEditLog && (
                                      <div className="small text-muted mt-1">
                                        <i className="bi bi-pencil me-1"></i>
                                        {log.changedBy} edited this order
                                        {matchingEdit && (
                                          <>
                                            {" — "}
                                            <a
                                              href="#"
                                              className="text-primary text-decoration-none"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                handleViewPreviousVersion(matchingEdit);
                                              }}
                                            >
                                              View previous version
                                            </a>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <i className="bi bi-calendar-event me-2 text-dark"></i>
                                    <span>
                                      {new Date(log.changeDate).toLocaleString("en-GB", {
                                        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit', hour12: true
                                      })}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-muted text-center my-3">No status changes recorded yet.</p>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Previous Version Modal */}
            <Modal show={showPreviousVersionModal} onHide={() => setShowPreviousVersionModal(false)} size="lg" centered>
              <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="h5">
                  <i className="bi bi-clock-history me-2"></i>
                  Previous Order Version
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {previousVersionLoading ? (
                  <div className="text-center p-4">
                    <Spinner animation="border" />
                  </div>
                ) : previousVersionData && schema ? (
                  <div>
                    {schema.sections.map((section, sectionIdx) => (
                      <div key={sectionIdx}>
                        <h6 className="text-muted border-bottom pb-2 mb-3">{section.title}</h6>
                        {section.fields.map((field, fieldIdx) => {
                          const value = previousVersionData[field.name];
                          if (value === undefined || value === null || value === "") return null;

                          // Skip image fields in modal
                          if (field.type === "file") return null;

                          return (
                            <Row key={fieldIdx} className="mb-2">
                              <Col md={4}>
                                <span className="fw-semibold text-muted small">{field.label}</span>
                              </Col>
                              <Col md={8}>
                                {field.type === "productList" || field.type === "inlineProductList" ? (
                                  <div>
                                    {Array.isArray(value) ? value.map((p: any, idx: number) => (
                                      <div key={idx} className="small">
                                        {p.name || p.code} x {p.quantity}
                                      </div>
                                    )) : <span className="small text-muted">-</span>}
                                  </div>
                                ) : (
                                  <span className="small">{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                                )}
                              </Col>
                            </Row>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted text-center">No previous version data available.</p>
                )}
              </Modal.Body>
              <Modal.Footer className="border-0 pt-0">
                <Button variant="secondary" size="sm" onClick={() => setShowPreviousVersionModal(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </PageContainer>
      ) : (
        <div id="printable-order-details" className="page-wrapper bg-white p-0">
          <div className="d-print-none bg-light border-bottom p-3 mb-4 d-flex justify-content-between align-items-center sticky-top">
            <h5 className="m-0">Print Preview: {schema.title}</h5>
            <div>
              <Button variant="success" size="sm" onClick={handlePrint} className="me-2">
                <i className="bi bi-printer me-1"></i> Print Now
              </Button>
              <Button variant="secondary" size="sm" onClick={() => window.close()}>
                Close
              </Button>
            </div>
          </div>

          <div className="sub-header-title d-block border-bottom-0 text-center">
            <span className="h3 d-block mb-0">{schema.title}</span>
          </div>

          <Form onSubmit={handleSubmit}>
            {schema.sections.map((section, sectionIdx) => (
              <div key={sectionIdx}>
                <h5 className="section-title">{section.title}</h5>
                {section.fields.map((field, fieldIdx) => (
                  <Row
                    key={fieldIdx}
                    className={`mb-2 ${field.type === "textarea" ? "align-items-start" : "align-items-center"}`}
                  >
                    <Col md={2}>
                      <Form.Label className="form-label-custom">{field.label}</Form.Label>
                    </Col>
                    <Col md={10}>
                      <FieldRenderer
                        field={field} value={formData[field.name] ?? ""} formData={formData}
                        setFormData={setFormData} onShowProductModal={() => setShowProductModal(true)}
                        availableProducts={schema.availableProducts} readOnly={isViewMode}
                        onShowImagePreview={(src) => setPreviewImage(src)}
                      />
                    </Col>
                  </Row>
                ))}
              </div>
            ))}
          </Form>
        </div>
      )}
      <ImagePreviewModal
        show={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageSrc={previewImage || ""}
      />
    </React.Fragment>
  );
};

export default DynamicOrderForm;
